<!--
  Created 13.10.2023
  Eateries component for the proposal show page.
-->
<template>
  <ProposalSection
    section="eateries"
    :content-editable="contentEditable"
    template-style="gray"
    :cta="(entities.filter(entity => !entity.alternative).length > 2 ? {
      text: 'Se flere',
      icon: ArrowRightIcon,
    }:{})"
    :cta-footer="(entities.filter(entity => entity.alternative).length > 0 ? {
      text: 'Se alternativer',
      icon: ArrowsPointingOutIcon,
      class: '!text-gray-600'
    }:{})"
    @heading-cta-clicked="myEntityCards.nextSlide()"
    @footer-cta-clicked="showModal=true">
    <template #content>
      <EntityCard
        v-if="entities.filter(entity => !entity.alternative).length === 1"
        :entity="entities.filter(entity => !entity.alternative)[0]"
        :content-editable="contentEditable"
        :string-limit="450"
        entities-type="eateries"
        :entity-index="
          noAltsToEntityIndex.size > 0
            ? noAltsToEntityIndex.get(entities.filter(entity => !entity.alternative)[0].id)
            : 0
        "
        is-rounded />
      <EntityCards
        v-else-if="entities.filter(entity => !entity.alternative).length > 1"
        ref="myEntityCards"
        :entities="entities.filter(entity => !entity.alternative)"
        :content-editable="contentEditable"
        :string-limit="450"
        :no-alts-to-entity-index="noAltsToEntityIndex"
        entities-type="eateries"
        is-rounded
        :is-carousel="entities.filter(entity => !entity.alternative).length > 2 && !store.printMode"
        carousel-bleed="right"
        grid-column-class="grid-cols-2" />
      <div v-else>
        <p class="text-lg italic text-gray-500">
          {{ contentEditable ? 'You have to add an eatery to your program for it to appear here.' : 'Det er ikke lagt inn noen spisesteder i programmet.' }}
        </p>
      </div>
      <div
        v-if="store.printMode && entities.filter(entity => entity.alternative).length > 0"
        class="print:break-before-page">
        <h2 class="text-xl font-medium print:mt-8">
          Alternative spisesteder
        </h2>
        <p class="mb-6 mt-4">
          Her er et utvalg av spisesteder som kan inkluderes i forslaget ditt. Vi har mange flere spisesteder å velge mellom, så ta gjerne kontakt med oss for å få flere forslag.
        </p>
        <EntityCards
          ref="myAlternativeEntityCards"
          :entities="entities.filter(entity => entity.alternative)"
          :content-editable="contentEditable"
          :string-limit="450"
          :alts-to-entity-index="altsToEntityIndex"
          entities-type="activities"
          is-rounded
          has-white-background
          grid-column-class="grid-cols-1" />
      </div>

      <!-- Alternative activities modal -->
      <SimpleModal
        box-class="w-11/12 md:w-4/5 lg:w-10/12 max-w-[1000px] max-h-[80vh] overflow-y-auto"
        :show="showModal"
        @update:show="(value) => showModal = value">
        <template #content>
          <div class="p-8">
            <h2 class="mb-4 text-2xl font-medium">
              Spisesteder
            </h2>
            <p class="mb-6">
              Her er et utvalg av spisesteder som kan inkluderes i forslaget ditt. Vi har mange flere spisesteder å velge mellom, så ta gjerne kontakt med oss for å få flere forslag.
            </p>
            <EntityCards
              v-if="showModal"
              ref="myAlternativeEntityCards"
              :entities="entities.filter(entity => entity.alternative)"
              :content-editable="contentEditable"
              :string-limit="450"
              :alts-to-entity-index="altsToEntityIndex"
              entities-type="eateries"
              has-white-background
              is-rounded
              grid-column-class="grid-cols-1" />
          </div>
        </template>
      </SimpleModal>
    </template>
  </ProposalSection>
</template>
<script setup>
import {
  ArrowRightIcon, ArrowsPointingOutIcon,
} from '@heroicons/vue/24/outline';
import ProposalSection from '@/Pages/Proposal/Components/ProposalSection.vue';
import EntityCards from '@/Pages/Proposal/Components/EntityCards.vue';
import EntityCard from '@/Pages/Proposal/Components/EntityCard.vue';
import {
  computed, ref,
} from 'vue';
import SimpleModal from '@/Components/Modals/SimpleModal.vue';
import { useProposalStore } from '@/Stores/Proposal';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

defineProps({
  contentEditable: {
    type: Boolean,
    default: false,
  },
});

const store = useProposalStore();
const entities = computed(() => store.entities.eateries);

const showModal = ref(false);
const myEntityCards = ref(null);

/**
 * Entity index mapping for non-alternative entities
 * @type {Map} - A map of entity id to original entity index
 */
const noAltsToEntityIndex = computed(() => {
  const mapping = new Map();
  entities.value.filter((entity) => !entity.alternative).forEach((entity) => {
    const originalIndex = entities.value.findIndex((e) => e.id === entity.id);
    mapping.set(entity.id, originalIndex);
  });

  return mapping;
});

/**
 * Entity index mapping for alternative entities
 * @type {Map} - A map of alternative entity id to original entity index
 */
const altsToEntityIndex = computed(() => {
  const mapping = new Map();
  entities.value.filter((entity) => entity.alternative).forEach((entity) => {
    const originalIndex = entities.value.findIndex((e) => e.id === entity.id);
    mapping.set(entity.id, originalIndex);
  });

  return mapping;
});

</script>
